import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import "./resourceTableModal.scss";
import IconButtons from "../IconButtons/IconButtons";
import {
  cross_default_icon,
  delete_active_icon,
  delete_disabled_icon,
  edit_active_icon,
  edit_disabled_icon,
} from "../../constants/icons";
import NoDataAvailable from "../NoDataAvailable/NoDataAvailable";
import {
  checkDateTimeStatus,
  font_13_icon,
  font_15,
  gitUrlPattern,
} from "../../utils/utils";
import { backendBaseUrl } from "../../config/config";
import TablePagination from "../TablePagination/TablePagination";
import Constants, { HelpTexts } from "../../constants/constants";
import Loader from "../Loader/Loader";
import BootstrapTooltip from "../BootstrapTooltip/BootstrapTooltip";
import CopyToClipboard from "../CopyToClipboard/CopyToClipboard";
import { margin } from "@mui/system";
const {
  RESOURCE_BELONGS_TO_TOPIC_NOT_DELETABLE,
  RESOURCE_BELONGS_TO_TOPIC_NOT_EDITABLE,
} = HelpTexts;
const onResourceViewClick = (url) => window.open(url, "_blank");

// Helper function to get delete button disabled state
const getDeleteButtonDisabled = (viaTopic, viaSession, data, isAllowed) => {
  if (viaTopic && !data?.permission?.canDeleteResource) return true;
  if (viaSession && (data?.type === Constants.TOPIC || !isAllowed)) return true;
  return false;
};

// Helper function to get delete button tooltip
const getDeleteButtonTooltip = (
  viaTopic,
  viaSession,
  data,
  isAllowed,
  isModuleConcluded
) => {
  if (viaTopic) {
    if (!data?.permission?.canDeleteResource) {
      return `This Resource can be deleted by SPOCs of ${data?.topic?.competency?.name} competency`;
    }
    return null;
  }
  if (viaSession) {
    if (data?.type === Constants.TOPIC) {
      return RESOURCE_BELONGS_TO_TOPIC_NOT_DELETABLE;
    }
    if (!isAllowed || isModuleConcluded) {
      return "Module is completed";
    }
    return null;
  }
  return null;
};

// Helper function to get edit button disabled state
const getEditButtonDisabled = (viaTopic, viaSession, data, isAllowed) => {
  if (viaTopic && !data?.permission?.canEditResource) return true;
  if (viaSession && (data?.type === Constants.TOPIC || !isAllowed)) return true;
  return false;
};

// Helper function to get edit button tooltip
const getEditButtonTooltip = (
  viaTopic,
  viaSession,
  data,
  isAllowed,
  isModuleConcluded
) => {
  if (viaTopic) {
    if (!data?.permission?.canEditResource) {
      return `This Resource can be edited by SPOCs of ${data?.topic?.competency?.name} competency`;
    }
    return null;
  }
  if (viaSession) {
    if (data?.type === Constants.TOPIC) {
      return RESOURCE_BELONGS_TO_TOPIC_NOT_EDITABLE;
    }
    if (!isAllowed || isModuleConcluded) {
      return "Module is completed";
    }
    return null;
  }
  return null;
};

// Helper function to get resource URL
const getResourceUrl = (data) => {
  if (data?.format === "URL") {
    return data?.url;
  }
  return `${backendBaseUrl}${data?.url}`;
};

// Helper function to get button text
const getButtonText = (format, isGitUrl) => {
  if (format === "URL") {
    return isGitUrl ? "Copy Link" : "View Link";
  }
  return "Download File";
};

// Component for Resource Link Cell
const ResourceLinkCell = ({ data, isGitUrl }) => {
  const resourceUrl = getResourceUrl(data);
  const buttonText = getButtonText(data?.format, isGitUrl);
  const tooltipTitle = data?.format === "URL" ? data?.url : "";

  return (
    <TableCell align="center">
      <CopyToClipboard text={isGitUrl ? data?.url : ""}>
        <BootstrapTooltip title={tooltipTitle}>
          <Button
            onClick={
              !isGitUrl ? () => onResourceViewClick(resourceUrl) : undefined
            }
            TypographyClasses
            color="secondary"
            rel="noopener noreferrer"
            variant="outlined"
            className="link-btn"
            sx={{
              width: "6.375rem",
              margin: "0 1rem",
            }}
          >
            <Typography
              variant="font_10_bold_700"
              sx={{
                textTransform: "uppercase",
                minWidth: "6.25rem",
              }}
            >
              {buttonText}
            </Typography>
          </Button>
        </BootstrapTooltip>
      </CopyToClipboard>
    </TableCell>
  );
};

// Component for Action Buttons
const ActionButtons = ({
  data,
  viaTopic,
  viaSession,
  isAllowed,
  isModuleConcluded,
  handleDeleteModalOpen,
  handleEditModalOpen,
}) => {
  const deleteDisabled = getDeleteButtonDisabled(
    viaTopic,
    viaSession,
    data,
    isAllowed
  );
  const deleteTooltip = getDeleteButtonTooltip(
    viaTopic,
    viaSession,
    data,
    isAllowed,
    isModuleConcluded
  );
  const editDisabled = getEditButtonDisabled(
    viaTopic,
    viaSession,
    data,
    isAllowed
  );
  const editTooltip = getEditButtonTooltip(
    viaTopic,
    viaSession,
    data,
    isAllowed,
    isModuleConcluded
  );

  return (
    <TableCell align="center" className="padding-left-1 padding-right-1">
      <Box>
        <IconButtons
          disabled={deleteDisabled}
          disabledTooltip={deleteTooltip}
          disabledImage={delete_disabled_icon}
          width={font_13_icon}
          height={font_13_icon}
          image={delete_active_icon}
          tooltip="Delete Resource"
          handleClick={() => handleDeleteModalOpen(data?.id, "Resource")}
        />
        <IconButtons
          disabled={editDisabled}
          disabledTooltip={editTooltip}
          disabledImage={edit_disabled_icon}
          width={font_13_icon}
          height={font_13_icon}
          image={edit_active_icon}
          tooltip="Edit Resource"
          handleClick={() => handleEditModalOpen(data)}
          style={{ marginLeft: "0.5rem" }}
        />
      </Box>
    </TableCell>
  );
};

// Component for Resource Table Row
const ResourceTableRow = ({
  data,
  viaTopic,
  viaSession,
  accessible,
  isAllowed,
  isModuleConcluded,
  handleDeleteModalOpen,
  handleEditModalOpen,
}) => {
  const isGitUrl = gitUrlPattern?.test(data?.url);
  const canShowActions =
    (viaTopic && data?.permission?.canViewDeleteResource) ||
    (viaSession && accessible);

  return (
    <TableRow key={data?.id} className="table-body-row">
      <TableCell
        component="th"
        scope="row"
        sx={{
          paddingLeft: "1rem !important",
          wordBreak: "break-word",
        }}
      >
        <Typography variant="font_12">{data?.name}</Typography>
      </TableCell>
      <ResourceLinkCell data={data} isGitUrl={isGitUrl} />
      {canShowActions && (
        <ActionButtons
          data={data}
          viaTopic={viaTopic}
          viaSession={viaSession}
          isAllowed={isAllowed}
          isModuleConcluded={isModuleConcluded}
          handleDeleteModalOpen={handleDeleteModalOpen}
          handleEditModalOpen={handleEditModalOpen}
        />
      )}
    </TableRow>
  );
};

const ResourceTableModal = ({
  open,
  handleClose,
  tableData,
  handleDeleteModalOpen,
  viaSession,
  viaTopic,
  isModuleConcluded,
  isAllowed,
  isResourceLoading,
  accessible = true,
  handleEditModalOpen,
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const totalResult = tableData?.totalResults;
  const handlePageChange = (value) => {
    setCurrentPage(value);
  };
  const tableList = useMemo(() => {
    setTotalPages(Math.ceil(totalResult / 5));
    const firstPageIndex = (currentPage - 1) * 5;
    const lastPageIndex = firstPageIndex + 5;
    return tableData?.results?.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, tableData]);

  const onClose = () => {
    setCurrentPage(1);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={onClose} className="resource-modal">
      {isResourceLoading ? (
        <Loader />
      ) : (
        <>
          <DialogTitle>
            <Box className="resource-title">
              <Typography variant="font_16_bold">Resources</Typography>
              <IconButtons
                height={font_15}
                width={font_15}
                image={cross_default_icon}
                handleClick={onClose}
                tooltip="Close"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {!isResourceLoading &&
            (!tableData || tableData?.totalResults === 0) ? (
              <NoDataAvailable
                imgHeight={4}
                imgWidth={4.5}
                message="No Resources Available"
              />
            ) : (
              <>
                <Grid container className="resource-table">
                  <TableContainer className="table-container">
                    <Table stickyHeader>
                      <TableHead className="table-head-container">
                        <TableRow className="table-head-row">
                          <TableCell
                            align="left"
                            sx={{
                              paddingLeft: "1rem !important",
                              wordBreak: "break-word",
                              minWidth: "12rem",
                            }}
                          >
                            <Typography variant="font_13_bold">Name</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: "8rem" }}>
                            <Typography variant="font_13_bold">
                              Resource Link
                            </Typography>
                          </TableCell>
                          {(viaTopic || (viaSession && accessible)) && (
                            <TableCell
                              align="center"
                              sx={{ minWidth: "6rem" }}
                              className="padding-left-1 padding-right-1"
                            >
                              <Typography variant="font_13_bold">
                                Actions
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tableList &&
                          tableList?.map((data) => (
                            <ResourceTableRow
                              key={data?.id}
                              data={data}
                              viaTopic={viaTopic}
                              viaSession={viaSession}
                              accessible={accessible}
                              isAllowed={isAllowed}
                              isModuleConcluded={isModuleConcluded}
                              handleDeleteModalOpen={handleDeleteModalOpen}
                              handleEditModalOpen={handleEditModalOpen}
                            />
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Grid container className="table-pagination-container">
                    <Typography variant="font_16">
                      Total {totalResult}{" "}
                      {totalResult > 1 ? "Records" : "Record"}
                    </Typography>
                    <TablePagination
                      totalPages={totalPages}
                      handlePageChange={handlePageChange}
                      currentPage={currentPage}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default ResourceTableModal;
